(() => {
  if (window.vapevoMobileCart) return;

  const strings = theme.mobileCartStrings;
  const pending = new WeakSet();
  const feedbacks = new WeakMap();
  const panels = new WeakMap();
  const pickers = new WeakMap();
  let queue = Promise.resolve();
  let activePanel = null;
  let activePicker = null;
  let panelNumber = 0;
  let pickerNumber = 0;
  let externalCartRevision = 0;
  let status;

  const setAttributeIfChanged = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const announce = (message) => {
    if (!status) {
      status = document.createElement('div');
      status.className = 'sr-only';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      document.body.append(status);
    }
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  const buttonsFor = (form) => [...new Set([
    ...form.querySelectorAll('button[type="submit"], input[type="submit"]'),
    ...(form.id ? document.querySelectorAll(`[type="submit"][form="${CSS.escape(form.id)}"]`) : [])
  ])];

  const clearFeedback = (button) => {
    const state = feedbacks.get(button);
    if (!state) return;
    clearTimeout(state.timer);
    state.element.remove();
    button.removeAttribute('data-vapevo-feedback');
    button.removeAttribute('data-vapevo-feedback-compact');
    if (state.label === null) button.removeAttribute('aria-label');
    else button.setAttribute('aria-label', state.label);
    feedbacks.delete(button);
  };

  const feedback = (button, phase) => {
    let state = feedbacks.get(button);
    if (!state) {
      const element = document.createElement('span');
      element.className = 'vapevo-mobile-feedback';
      element.setAttribute('aria-hidden', 'true');
      state = { element, label: button.getAttribute('aria-label'), timer: null };
      feedbacks.set(button, state);
      button.append(element);
    }
    clearTimeout(state.timer);
    const compact = !!button.closest('.quick-add') &&
      (phase !== 'success' || window.matchMedia('(max-width: 767px)').matches);
    button.toggleAttribute('data-vapevo-feedback-compact', compact);
    button.setAttribute('data-vapevo-feedback', phase);
    button.setAttribute('aria-label', phase === 'success' ? strings.addedLabel : strings.adding);
    state.element.replaceChildren();
    const icon = document.createElement('span');
    icon.className = phase === 'success' ? 'vapevo-mobile-check' : 'vapevo-mobile-spinner';
    state.element.append(icon);
    if (!compact) state.element.append(document.createTextNode(phase === 'success' ? strings.added : strings.adding));
    if (phase === 'success') state.timer = setTimeout(() => clearFeedback(button), 1800);
  };

  const errorFor = (form) => {
    let element = form.querySelector('.product-form__error-message');
    if (element) return element;
    const card = form.closest('[data-vapevo-card]');
    if (card) {
      element = card.querySelector('.vapevo-mobile-card-error');
      if (!element) {
        element = document.createElement('div');
        element.className = 'vapevo-mobile-card-error';
        (card.querySelector('.product-card__media') || card).append(element);
      }
    } else {
      element = document.createElement('div');
      element.className = 'product-form__error-message alert alert--error text-sm';
      form.prepend(element);
    }
    element.setAttribute('role', 'alert');
    return element;
  };

  const setError = (form, message = '') => {
    const element = errorFor(form);
    element.hidden = !message;
    element.textContent = message;
  };

  const getCart = async () => {
    const response = await fetch(`${theme.routes.cart_url.replace(/\.js$/, '')}.js`, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart refresh failed');
    return response.json();
  };

  const freshDrawer = async (drawer) => {
    const url = new URL(theme.routes.root_url, window.location.origin);
    url.searchParams.set('section_id', drawer.getAttribute('data-section-id'));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Drawer refresh failed');
    return response.text();
  };

  const refreshWithoutOpening = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
  };

  const syncCart = async (job, added, attempt = 0) => {
    const revision = externalCartRevision;
    const cart = await getCart();
    const drawer = document.querySelector('cart-drawer');
    const sectionId = drawer?.getAttribute('data-section-id');
    let section = sectionId ? added?.sections?.[sectionId] : null;
    if (drawer && (!section || drawer.hasAttribute('open') || revision !== job.revision)) {
      section = await freshDrawer(drawer);
    }
    if (externalCartRevision !== revision) {
      if (attempt < 2) return syncCart(job, null, attempt + 1);
      refreshWithoutOpening();
      const latest = await getCart();
      delete latest.sections;
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form', productVariantId: job.variantId, cart: latest, vapevoMobile: true
      });
      return;
    }
    if (drawer && section) {
      drawer.refreshAbortController?.abort();
      const id = `MiniCart-${sectionId}`;
      const current = document.getElementById(id);
      const updated = new DOMParser().parseFromString(section, 'text/html').getElementById(id);
      if (current && updated) current.innerHTML = updated.innerHTML;
      else refreshWithoutOpening();
    }
    delete cart.sections;
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-form', productVariantId: job.variantId, cart, vapevoMobile: true
    });
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: { ...cart, ...(sectionId && section ? { sections: { [sectionId]: section } } : {}) } }
    }));
  };

  const reconcileCart = async (job, added) => {
    try {
      await syncCart(job, added);
    } catch (error) {
      try {
        await syncCart(job);
      } catch (refreshError) {
        refreshWithoutOpening();
        const retryWhenOnline = () => {
          syncCart(job).catch(() => {});
          window.removeEventListener('online', retryWhenOnline);
        };
        window.addEventListener('online', retryWhenOnline, { once: true });
      }
    }
  };

  const performAdd = async (job) => {
    let confirmed = false;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true, detail: { sections }
      }));
      const requestedSections = [...new Set(sections)].slice(0, 5);
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      let body = job.body;
      if (job.json) {
        body = JSON.stringify({ ...body, sections: requestedSections, sections_url: job.path });
        headers['Content-Type'] = 'application/json';
      } else {
        body.set('sections', requestedSections.join(','));
        body.set('sections_url', job.path);
      }
      job.revision = externalCartRevision;
      const response = await fetch(theme.routes.cart_add_url, { method: 'POST', credentials: 'same-origin', headers, body });
      const added = await response.json();
      if (!response.ok || added.status) {
        const message = typeof added.description === 'string' ? added.description : strings.addError;
        setError(job.form, message);
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
          source: 'product-form', productVariantId: job.variantId, errors: added.errors || message, message: added.message
        });
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: message } }));
        await reconcileCart(job);
        return;
      }
      confirmed = true;
      await reconcileCart(job, added);
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added } }));
      job.buttons.forEach(button => feedback(button, 'success'));
      announce(`${job.title ? `${job.title} : ` : ''}${strings.addedLabel}`);
    } catch (error) {
      if (!confirmed) {
        setError(job.form, strings.addError);
        await reconcileCart(job);
      }
    } finally {
      pending.delete(job.form);
      job.buttons.forEach(button => {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        if (!confirmed) clearFeedback(button);
      });
    }
  };

  const handlesForm = (form) => !document.body.classList.contains('template-cart') &&
    form instanceof HTMLFormElement && !!form.closest('#MainContent') &&
    (form.matches('[is="product-form"]') || form.hasAttribute('data-vapevo-mobile-form'));

  const handlesTrigger = (trigger) => !!trigger?.closest('#MainContent') && !document.body.classList.contains('template-cart');

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!handlesForm(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (pending.has(form) || submitter?.disabled || submitter?.getAttribute('aria-disabled') === 'true') return;
    const data = new FormData(form);
    const variantId = data.get('id');
    if (!variantId) return;
    const buttons = buttonsFor(form);
    const json = typeof form.prepareFormData === 'function' && form.bundles?.length > 0;
    const job = {
      form, buttons, variantId, body: json ? form.prepareFormData(data) : data, json,
      path: window.location.pathname,
      title: form.closest('[data-vapevo-card]')?.getAttribute('data-vapevo-product-title') || ''
    };
    pending.add(form);
    setError(form);
    buttons.forEach(button => {
      clearFeedback(button);
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      feedback(button, 'loading');
    });
    queue = queue.then(() => performAdd(job)).catch(() => {});
  }, true);

  const pickerFor = (element) => pickers.get(element?.closest('.vapevo-select'));

  const selectableOptions = (picker) => Array.from(picker.select.options).filter(option =>
    !option.disabled && !option.parentElement?.disabled);

  const keepOptionVisible = (picker, item) => {
    for (const container of [picker.list, picker.select.closest('.vapevo-quick-options'), picker.select.closest('.vapevo-quick-panel__body')]) {
      if (!container?.clientHeight) continue;
      const rect = item.getBoundingClientRect();
      const top = container.getBoundingClientRect().top + container.clientTop;
      if (rect.top < top) container.scrollTop += rect.top - top;
      else if (rect.bottom > top + container.clientHeight) container.scrollTop += rect.bottom - top - container.clientHeight;
    }
  };

  const setActiveOption = (picker, value, scroll = false) => {
    picker.activeValue = value;
    let active;
    for (const item of picker.list.children) {
      const matches = activePicker === picker && item.getAttribute('data-value') === value;
      item.toggleAttribute('data-active', matches);
      if (matches) active = item;
    }
    if (active) {
      setAttributeIfChanged(picker.button, 'aria-activedescendant', active.id);
      if (scroll) keepOptionVisible(picker, active);
    } else picker.button.removeAttribute('aria-activedescendant');
  };

  const closePicker = () => {
    if (!activePicker) return;
    const picker = activePicker;
    activePicker = null;
    picker.list.hidden = true;
    picker.button.setAttribute('aria-expanded', 'false');
    setActiveOption(picker, picker.select.value);
  };

  const refreshPicker = (picker) => {
    const items = new Map(Array.from(picker.list.children, item => [item.getAttribute('data-value'), item]));
    const retained = new Set();
    Array.from(picker.select.options).forEach((option, index) => {
      let item = items.get(option.value);
      if (!item) {
        item = document.createElement('div');
        item.id = `${picker.list.id}-option-${++picker.itemNumber}`;
        item.className = 'vapevo-select__option';
        item.setAttribute('role', 'option');
        item.setAttribute('data-value', option.value);
      }
      retained.add(item);
      if (picker.list.children[index] !== item) picker.list.insertBefore(item, picker.list.children[index] || null);
      if (item.textContent !== option.textContent) item.textContent = option.textContent;
      setAttributeIfChanged(item, 'aria-selected', String(option.selected));
      setAttributeIfChanged(item, 'aria-disabled', String(option.disabled || !!option.parentElement?.disabled));
      if (option.hasAttribute('data-available')) setAttributeIfChanged(item, 'data-available', option.getAttribute('data-available'));
      else item.removeAttribute('data-available');
    });
    Array.from(picker.list.children).forEach(item => { if (!retained.has(item)) item.remove(); });
    const label = picker.select.selectedOptions[0]?.textContent || '';
    if (picker.value.textContent !== label) picker.value.textContent = label;
    const disabled = picker.select.disabled || picker.select.options.length === 0;
    if (picker.button.disabled !== disabled) picker.button.disabled = disabled;
    if (!picker.label) setAttributeIfChanged(picker.button, 'aria-label', picker.select.getAttribute('aria-label') || '');
    const enabled = selectableOptions(picker);
    const value = activePicker === picker && enabled.some(option => option.value === picker.activeValue)
      ? picker.activeValue : enabled.find(option => option.selected)?.value || enabled[0]?.value;
    setActiveOption(picker, value);
  };

  const openPicker = (picker) => {
    if (picker.button.disabled) return;
    closePicker();
    activePicker = picker;
    picker.search = '';
    picker.searchAt = 0;
    picker.list.hidden = false;
    picker.button.setAttribute('aria-expanded', 'true');
    if (document.activeElement !== picker.button) picker.button.focus({ preventScroll: true });
    const options = selectableOptions(picker);
    setActiveOption(picker, options.find(option => option.selected)?.value || options[0]?.value, true);
  };

  const commitPicker = (picker, value = picker.activeValue, restoreFocus = true) => {
    const option = selectableOptions(picker).find(candidate => candidate.value === value);
    if (!option) return;
    const changed = picker.select.value !== option.value;
    picker.select.value = option.value;
    closePicker();
    if (restoreFocus && document.activeElement !== picker.button) picker.button.focus({ preventScroll: true });
    if (changed) picker.select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const pickerKeydown = (picker, event) => {
    if (picker.button.disabled) return false;
    const isOpen = activePicker === picker;
    if (event.key === 'Tab') {
      if (isOpen) {
        commitPicker(picker, picker.activeValue, false);
        closePicker();
      }
      return false;
    }
    const navigate = ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key);
    const activate = event.key === 'Enter' || event.key === ' ';
    const search = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!navigate && !activate && !search) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (activate) {
      if (!event.repeat) isOpen ? commitPicker(picker) : openPicker(picker);
      return true;
    }
    if (!isOpen) openPicker(picker);
    const options = selectableOptions(picker);
    if (!options.length) return true;
    const current = options.findIndex(option => option.value === picker.activeValue);
    if (navigate) {
      let index = current;
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = options.length - 1;
      else if (isOpen) index += event.key === 'ArrowDown' ? 1 : -1;
      setActiveOption(picker, options[Math.max(0, Math.min(options.length - 1, index))].value, true);
      picker.search = '';
    } else {
      const now = Date.now();
      picker.search = now - picker.searchAt < 700 ? picker.search + event.key.toLocaleLowerCase() : event.key.toLocaleLowerCase();
      picker.searchAt = now;
      const repeated = Array.from(picker.search).every(character => character === picker.search[0]);
      const query = repeated ? picker.search[0] : picker.search;
      const start = current + (query.length === 1 ? 1 : 0);
      const ordered = options.map((_, offset) => options[(Math.max(0, start) + offset) % options.length]);
      const match = ordered.find(option => option.textContent.trim().toLocaleLowerCase().startsWith(query));
      if (match) setActiveOption(picker, match.value, true);
    }
    return true;
  };

  const enhanceOptions = (root) => {
    root.querySelectorAll('select[data-vapevo-option]').forEach(select => {
      if (pickers.has(select)) return;
      if (!select.options.length) return;
      const id = `VapevoSelect-${++pickerNumber}`;
      const wrapper = document.createElement('div');
      wrapper.className = 'vapevo-select';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vapevo-select__button';
      button.setAttribute('role', 'combobox');
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      const value = document.createElement('span');
      value.className = 'vapevo-select__value';
      button.append(value);
      const list = document.createElement('div');
      list.id = `${id}-list`;
      list.className = 'vapevo-select__list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      button.setAttribute('aria-controls', list.id);
      const label = select.closest('.vapevo-quick-option')?.querySelector('.vapevo-quick-option__label');
      if (label) {
        if (!label.id) label.id = `${id}-label`;
        button.setAttribute('aria-labelledby', label.id);
        list.setAttribute('aria-labelledby', label.id);
      }
      const picker = { select, wrapper, button, value, list, label, itemNumber: 0 };
      wrapper.append(button, list);
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        activePicker === picker ? closePicker() : openPicker(picker);
      });
      list.addEventListener('mousedown', event => {
        if (event.target.closest('[role="option"]')) event.preventDefault();
      });
      list.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (!item || !list.contains(item)) return;
        event.preventDefault();
        event.stopPropagation();
        commitPicker(picker, item.getAttribute('data-value'));
      });
      refreshPicker(picker);
      select.after(wrapper);
      pickers.set(select, picker);
      pickers.set(wrapper, picker);
      select.hidden = true;
    });
  };

  const muteCard = (state) => {
    for (const child of state.card.children) {
      if (child === state.panel) continue;
      if (!state.inertSiblings.has(child)) state.inertSiblings.set(child, child.getAttribute('inert'));
      if (!child.hasAttribute('inert')) child.setAttribute('inert', '');
    }
  };

  const closePanel = (restoreFocus = false) => {
    if (!activePanel) return;
    closePicker();
    const state = activePanel;
    activePanel = null;
    state.controller?.abort();
    state.panel.hidden = true;
    state.card.removeAttribute('data-vapevo-panel-open');
    state.trigger.setAttribute('aria-expanded', 'false');
    for (const [element, value] of state.inertSiblings) {
      if (value === null) element.removeAttribute('inert');
      else element.setAttribute('inert', value);
    }
    state.inertSiblings.clear();
    if (restoreFocus && state.trigger.isConnected) state.trigger.focus({ preventScroll: true });
  };

  const syncAttributes = (target, source, preserve = []) => {
    for (const attribute of Array.from(target.attributes)) {
      if (!preserve.includes(attribute.name) && !source.hasAttribute(attribute.name)) target.removeAttribute(attribute.name);
    }
    for (const attribute of source.attributes) {
      if (!preserve.includes(attribute.name) && target.getAttribute(attribute.name) !== attribute.value) target.setAttribute(attribute.name, attribute.value);
    }
  };

  const syncSelect = (target, source) => {
    syncAttributes(target, source, pickers.has(target) ? ['hidden'] : []);
    const current = new Map(Array.from(target.options, option => [option.value, option]));
    const retained = new Set();
    Array.from(source.options).forEach((incoming, index) => {
      const option = current.get(incoming.value) || incoming.cloneNode(true);
      retained.add(option);
      if (target.options[index] !== option) target.insertBefore(option, target.options[index] || null);
      syncAttributes(option, incoming);
      if (option.textContent !== incoming.textContent) option.textContent = incoming.textContent;
    });
    Array.from(target.options).forEach(option => { if (!retained.has(option)) option.remove(); });
    if (target.selectedIndex !== source.selectedIndex) target.selectedIndex = source.selectedIndex;
    const label = target.closest('.vapevo-quick-option')?.querySelector('.vapevo-quick-option__label');
    const incomingLabel = source.closest('.vapevo-quick-option')?.querySelector('.vapevo-quick-option__label');
    if (label && incomingLabel && label.textContent !== incomingLabel.textContent) label.textContent = incomingLabel.textContent;
    if (pickers.has(target)) refreshPicker(pickers.get(target));
  };

  const updatePanelContent = (current, incoming) => {
    if (!current || current.getAttribute('data-product-url') !== incoming.getAttribute('data-product-url')) return false;
    const form = current.querySelector('[data-vapevo-mobile-form]');
    const incomingForm = incoming.querySelector('[data-vapevo-mobile-form]');
    const options = current.querySelector('.vapevo-quick-options');
    const incomingOptions = incoming.querySelector('.vapevo-quick-options');
    const price = current.querySelector('[data-vapevo-quick-price]');
    const incomingPrice = incoming.querySelector('[data-vapevo-quick-price]');
    const button = form?.querySelector('[type="submit"], .vapevo-mobile-add');
    const incomingButton = incomingForm?.querySelector('[type="submit"], .vapevo-mobile-add');
    const label = button?.querySelector('.btn-text');
    const incomingLabel = incomingButton?.querySelector('.btn-text');
    if (!form || !incomingForm || !options || !incomingOptions || !price || !incomingPrice ||
      !button || !incomingButton || button.tagName !== incomingButton.tagName || !label || !incomingLabel) return false;
    if (options.parentElement !== form || incomingOptions.parentElement !== incomingForm) return false;
    const selects = Array.from(options.querySelectorAll('[data-vapevo-option]'));
    const incomingSelects = Array.from(incomingOptions.querySelectorAll('[data-vapevo-option]'));
    if (selects.length !== incomingSelects.length || selects.some((select, index) =>
      select.getAttribute('data-option-position') !== incomingSelects[index].getAttribute('data-option-position'))) return false;
    const inputs = Array.from(form.querySelectorAll('input[name]'));
    const incomingInputs = Array.from(incomingForm.querySelectorAll('input[name]'));
    if ([...inputs, ...incomingInputs].some(input => input.type !== 'hidden' ||
      input.parentElement !== input.closest('[data-vapevo-mobile-form]'))) return false;
    if ([form, incomingForm].some(element => element.querySelector('textarea, select:not([data-vapevo-option])'))) return false;

    syncAttributes(current, incoming);
    syncAttributes(form, incomingForm);
    syncAttributes(options, incomingOptions);
    syncAttributes(price, incomingPrice);
    if (price.innerHTML !== incomingPrice.innerHTML) price.innerHTML = incomingPrice.innerHTML;
    selects.forEach((select, index) => syncSelect(select, incomingSelects[index]));
    const retainedInputs = new Set();
    for (const incomingInput of incomingInputs) {
      const input = inputs.find(candidate => !retainedInputs.has(candidate) && candidate.name === incomingInput.name) || incomingInput.cloneNode(true);
      retainedInputs.add(input);
      syncAttributes(input, incomingInput);
      if (input.value !== incomingInput.value) input.value = incomingInput.value;
      if (input.parentElement !== form) form.insertBefore(input, options);
    }
    inputs.forEach(input => { if (!retainedInputs.has(input)) input.remove(); });
    const feedbackState = feedbacks.get(button);
    if (feedbackState) feedbackState.label = incomingButton.getAttribute('aria-label');
    syncAttributes(button, incomingButton, [
      'aria-busy', 'aria-disabled', 'data-vapevo-feedback', 'data-vapevo-feedback-compact',
      ...(feedbackState ? ['aria-label'] : [])
    ]);
    syncAttributes(label, incomingLabel);
    if (label.innerHTML !== incomingLabel.innerHTML) label.innerHTML = incomingLabel.innerHTML;
    const error = form.querySelector('.product-form__error-message');
    const incomingError = incomingForm.querySelector('.product-form__error-message');
    if (error && incomingError) {
      syncAttributes(error, incomingError);
      error.textContent = incomingError.textContent;
    }
    return true;
  };

  const loadPanel = async (state, optionIds, productUrl, focusPosition) => {
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    const existing = state.body.querySelector('[data-vapevo-quick-content]');
    const oldButton = existing?.querySelector('[type="submit"]');
    if (oldButton) oldButton.disabled = true;
    state.body.setAttribute('aria-busy', 'true');
    if (!existing) state.body.textContent = strings.loading;
    try {
      const url = new URL(productUrl || state.url, window.location.origin);
      url.searchParams.delete('variant');
      url.searchParams.set('section_id', 'vapevo-mobile-quick-add');
      if (optionIds?.length) url.searchParams.set('option_values', optionIds.join(','));
      else url.searchParams.delete('option_values');
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Variant options unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-vapevo-quick-content]');
      if (!content) throw new Error('Variant options missing');
      if (controller.signal.aborted || !state.panel.isConnected || state !== activePanel) return;
      const focusedSelect = pickerFor(document.activeElement)?.select || document.activeElement.closest('[data-vapevo-option]');
      const focusedPosition = existing?.contains(document.activeElement) ? focusedSelect?.getAttribute('data-option-position') : null;
      const scroll = existing?.querySelector('.vapevo-quick-options')?.scrollTop || 0;
      const preserved = updatePanelContent(existing, content);
      if (!preserved) {
        closePicker();
        state.body.replaceChildren(content);
      }
      state.body.querySelectorAll('.vapevo-quick-load-error').forEach(element => element.remove());
      state.url = content.getAttribute('data-product-url') || state.url;
      const rendered = preserved ? existing : content;
      enhanceOptions(rendered);
      const options = rendered.querySelector('.vapevo-quick-options');
      if (options && options.scrollTop !== scroll) options.scrollTop = scroll;
      if (!preserved && focusedPosition) {
        const select = rendered.querySelector(`select[data-option-position="${CSS.escape(focusedPosition)}"]`);
        (pickers.get(select)?.button || select || state.panel).focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted || !state.panel.isConnected || state !== activePanel) return;
      let message = state.body.querySelector('.vapevo-quick-load-error');
      if (!message) {
        message = document.createElement('div');
        message.className = 'vapevo-quick-load-error';
        message.setAttribute('role', 'alert');
        if (!existing) state.body.replaceChildren();
        state.body.append(message);
      }
      const text = document.createElement('p');
      text.textContent = strings.loadError;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'vapevo-quick-retry';
      retry.textContent = strings.retry;
      retry.addEventListener('click', () => loadPanel(state, optionIds, productUrl, focusPosition), { once: true });
      message.replaceChildren(text, retry);
    } finally {
      if (state.controller === controller) state.body.removeAttribute('aria-busy');
    }
  };

  const openPanel = (trigger) => {
    const card = trigger.closest('[data-vapevo-card]');
    if (!card) return;
    if (activePanel?.card === card) {
      closePanel(true);
      return;
    }
    closePanel();
    let state = panels.get(card);
    if (!state) {
      const panel = document.createElement('section');
      panel.id = `VapevoQuickPanel-${++panelNumber}`;
      panel.className = 'vapevo-quick-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'false');
      panel.setAttribute('aria-label', `${strings.selectOptions} — ${card.getAttribute('data-vapevo-product-title')}`);
      panel.tabIndex = -1;
      const header = document.createElement('div');
      header.className = 'vapevo-quick-panel__header';
      const title = document.createElement('span');
      title.className = 'vapevo-quick-panel__title';
      title.textContent = strings.selectOptions;
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'vapevo-quick-panel__close';
      close.setAttribute('aria-label', theme.strings.close);
      close.textContent = '×';
      close.addEventListener('click', () => closePanel(true));
      header.append(title, close);
      const body = document.createElement('div');
      body.className = 'vapevo-quick-panel__body';
      panel.append(header, body);
      card.append(panel);
      state = { panel, body, card, trigger, inertSiblings: new Map(), url: card.getAttribute('data-vapevo-product-url') };
      panels.set(card, state);
    }
    state.trigger = trigger;
    activePanel = state;
    state.panel.hidden = false;
    card.setAttribute('data-vapevo-panel-open', '');
    trigger.setAttribute('aria-controls', state.panel.id);
    trigger.setAttribute('aria-expanded', 'true');
    state.panel.focus({ preventScroll: true });
    muteCard(state);
    const optionIds = Array.from(state.body.querySelectorAll('[data-vapevo-option]'), select => select.value);
    loadPanel(state, optionIds);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-vapevo-quick-open]');
    if (handlesTrigger(trigger) && trigger.closest('[data-vapevo-card]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openPanel(trigger);
      return;
    }
    if (activePicker && !activePicker.wrapper.contains(event.target)) closePicker();
    if (activePanel && !activePanel.panel.contains(event.target)) closePanel();
  }, true);

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-vapevo-option]');
    if (!select || !activePanel?.panel.contains(select)) return;
    if (pickers.has(select)) refreshPicker(pickers.get(select));
    const optionIds = Array.from(activePanel.body.querySelectorAll('[data-vapevo-option]'), option => option.value);
    const productUrl = select.selectedOptions[0]?.getAttribute('data-product-url');
    const linkedProduct = productUrl && new URL(productUrl, window.location.origin).pathname !== new URL(activePanel.url, window.location.origin).pathname;
    loadPanel(activePanel, linkedProduct ? null : optionIds, productUrl, select.getAttribute('data-option-position'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activePicker) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closePicker();
    } else if (pickerFor(event.target) && pickerKeydown(pickerFor(event.target), event)) {
      return;
    } else if (event.key === 'Escape' && activePanel) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closePanel(true);
    } else if (event.key === ' ' && event.target.matches('a[data-vapevo-quick-open]') && handlesTrigger(event.target)) {
      event.preventDefault();
      openPanel(event.target);
    }
  }, true);

  document.addEventListener('focusin', event => {
    if (activePicker && !activePicker.wrapper.contains(event.target)) closePicker();
  });

  const enhanceTrigger = (trigger) => {
    if (!handlesTrigger(trigger)) return;
    if (trigger.tagName === 'A') setAttributeIfChanged(trigger, 'role', 'button');
    setAttributeIfChanged(trigger, 'aria-haspopup', 'dialog');
    if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
    setAttributeIfChanged(trigger, 'aria-label', `${strings.selectOptions} — ${trigger.closest('[data-vapevo-card]')?.getAttribute('data-vapevo-product-title') || ''}`);
  };

  const enhance = (root) => {
    if (root.matches('[data-vapevo-quick-open]')) enhanceTrigger(root);
    if (root.firstElementChild) root.querySelectorAll('[data-vapevo-quick-open]').forEach(enhanceTrigger);
  };

  const main = document.getElementById('MainContent');
  if (main && !document.body.classList.contains('template-cart')) {
    enhance(main);
    new MutationObserver(mutations => {
      const roots = new Set();
      for (const mutation of mutations) for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1 || !node.isConnected || !main.contains(node) ||
          node.closest('.vapevo-quick-panel, .vapevo-mobile-feedback, .vapevo-mobile-card-error')) continue;
        roots.add(node);
      }
      for (const root of roots) {
        let covered = false;
        for (let parent = root.parentElement; parent && parent !== main; parent = parent.parentElement) {
          if (roots.has(parent)) { covered = true; break; }
        }
        if (!covered) enhance(root);
      }
      if (activePanel && (!activePanel.panel.isConnected || !main.contains(activePanel.card))) closePanel();
      else if (activePanel && mutations.some(mutation => mutation.target === activePanel.card)) muteCard(activePanel);
    }).observe(main, { childList: true, subtree: true });
  }
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, event => {
    if (!event.vapevoMobile) externalCartRevision++;
  });
  window.vapevoMobileCart = { version: '1.3.0' };
})();
